import { parseTeamNumber, teamSyncoreWildcard } from "@/grid/lib/team-syncore-ref";
import { getMtdPacingTarget, getProratedWeeklyTarget, getQtdPacingTarget } from "@/grid/lib/pacing";
import {
  allMonthStartsInCalendarQuarter,
  allMonthStartsInCalendarYear,
  monthStart,
  monthStartForWeekMonday,
  priorWeekMonday,
  quarterStart,
  quarterStartsInCalendarYear,
  weekStartMonday,
  weekStartsOverlappingMonth,
  weekStartsOverlappingMonthThrough,
  weekStartsOverlappingQuarter,
  weekStartsOverlappingQuarterThrough,
  weekFractionInMonth,
  weekFractionInMonthThrough,
  weekFractionInQuarter,
  weekFractionInQuarterThrough,
  sumWeekFractionsInMonth,
  nowToronto,
} from "@/grid/core/periods";
import { parseISO } from "date-fns";
import {
  RECURRING_WEEK_PERIOD_START,
  RECURRING_WEEK_PERIOD_TYPE,
} from "@/grid/core/target-periods";
import {
  columnUsesMonthlyLookup,
  columnUsesQuarterlyLookup,
  rollupRuleForColumn,
  type TargetMetricConfig,
} from "@/grid/core/target-policy";
import type {
  MetricDefinition,
  PeriodId,
  TargetPeriodType,
  TargetRow,
  TargetSource,
} from "@/grid/core/types";

export interface TargetScope {
  departmentId: string;
  teamGroupKey: string | null;
  teamId: number | null;
}

export interface ResolvedTarget {
  target: number | null;
  pacingTarget: number | null;
  targetSource: TargetSource | null;
}

function targetKey(
  scopeType: string,
  scopeKey: string | null,
  scopeId: number | null,
  metric: string,
  periodType: string,
  periodStart: string,
): string {
  return [scopeType, scopeKey ?? "", scopeId ?? 0, metric, periodType, periodStart].join("|");
}

export function indexTargets(rows: TargetRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    map.set(
      targetKey(
        row.scope_type,
        row.scope_key,
        row.scope_id,
        row.metric,
        row.period_type,
        row.period_start,
      ),
      row.target_value,
    );

    if (row.scope_type === "team_group" && row.scope_key) {
      const teamNumber = parseTeamNumber(row.scope_key);
      if (teamNumber != null) {
        const wildcardKey = targetKey(
          "team_group",
          teamSyncoreWildcard(teamNumber),
          null,
          row.metric,
          row.period_type,
          row.period_start,
        );
        if (!map.has(wildcardKey)) {
          map.set(wildcardKey, row.target_value);
        }
      }
    }
  }
  return map;
}

function lookupTargetAtPeriod(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  periodType: TargetPeriodType,
  periodStart: string,
): number | null {
  const attempts: Array<[string, string | null, number | null]> = [
    ["team_group", scope.teamGroupKey, null],
    ["department", scope.departmentId, null],
    ["team", null, scope.teamId],
  ];

  for (const [scopeType, scopeKey, scopeId] of attempts) {
    if (scopeType === "team_group" && !scopeKey) continue;
    if (scopeType === "team" && scopeId == null) continue;

    const value = index.get(
      targetKey(scopeType, scopeKey, scopeId, metric, periodType, periodStart),
    );
    if (value != null) return value;
  }

  return null;
}

function lookupWeeklyTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  periodStart: string,
): { value: number; source: TargetSource } | null {
  const explicit = lookupTargetAtPeriod(index, scope, metric, "week", periodStart);
  if (explicit != null) return { value: explicit, source: "explicit" };

  const recurring = lookupTargetAtPeriod(
    index,
    scope,
    metric,
    RECURRING_WEEK_PERIOD_TYPE,
    RECURRING_WEEK_PERIOD_START,
  );
  if (recurring != null) return { value: recurring, source: "default" };

  return null;
}

interface RolledUpTarget {
  target: number;
  pacingTarget: number;
  source: TargetSource;
}

function sumWeeklyTargetsForWeeks(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  weekStarts: string[],
  weekWeight: (monday: string) => number,
): { target: number; source: TargetSource } | null {
  let total = 0;
  let hasValue = false;
  let allDefault = true;

  for (const monday of weekStarts) {
    const weight = weekWeight(monday);
    if (weight <= 0) continue;

    const weekly = lookupWeeklyTarget(index, scope, metric, monday);
    if (!weekly) continue;
    total += weekly.value * weight;
    hasValue = true;
    if (weekly.source === "explicit") allDefault = false;
  }

  if (!hasValue) return null;

  return {
    target: total,
    source: allDefault ? "default" : "explicit",
  };
}

type WeeklyRollupPeriod = "month" | "quarter";

function rollupWeeklyTargets(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  allWeekStarts: string[],
  elapsedWeekStarts: string[],
  asOf: Date,
  period: WeeklyRollupPeriod,
): RolledUpTarget | null {
  const fullWeight =
    period === "month"
      ? (monday: string) => weekFractionInMonth(monday, asOf)
      : (monday: string) => weekFractionInQuarter(monday, asOf);
  const throughWeight =
    period === "month"
      ? (monday: string) => weekFractionInMonthThrough(monday, asOf)
      : (monday: string) => weekFractionInQuarterThrough(monday, asOf);

  const full = sumWeeklyTargetsForWeeks(index, scope, metric, allWeekStarts, fullWeight);
  if (!full) return null;

  const thisMonday = weekStartMonday(asOf);
  let pacingTarget = 0;
  let hasPacing = false;

  for (const monday of elapsedWeekStarts) {
    const weekly = lookupWeeklyTarget(index, scope, metric, monday);
    if (!weekly) continue;

    const fractionFull = fullWeight(monday);
    if (fractionFull <= 0) continue;

    hasPacing = true;
    if (monday < thisMonday) {
      pacingTarget += weekly.value * fractionFull;
      continue;
    }

    // Current week: calendar proration when the week straddles a period boundary.
    if (fractionFull < 1) {
      pacingTarget += weekly.value * throughWeight(monday);
    } else {
      pacingTarget += getProratedWeeklyTarget(weekly.value, asOf);
    }
  }

  return {
    target: full.target,
    pacingTarget: hasPacing ? pacingTarget : full.target,
    source: "inferred",
  };
}

function sumMonthlyTargetsForQuarter(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  asOf: Date,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const monthStartDate of allMonthStartsInCalendarQuarter(asOf)) {
    const value = lookupTargetAtPeriod(index, scope, metric, "month", monthStartDate);
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

/** Sum each month's pace target: full month for elapsed months, MTD proration for current. */
function sumMonthlyPacingTargetsForQuarter(
  index: Map<string, number>,
  scope: TargetScope,
  metric: string,
  asOf: Date,
): number | null {
  const currentMonthStart = monthStart(asOf);
  let total = 0;
  let hasValue = false;

  for (const monthStartDate of allMonthStartsInCalendarQuarter(asOf)) {
    const value = lookupTargetAtPeriod(index, scope, metric, "month", monthStartDate);
    if (value == null) continue;
    hasValue = true;

    if (monthStartDate < currentMonthStart) {
      total += value;
    } else if (monthStartDate === currentMonthStart) {
      total += getMtdPacingTarget(value, asOf);
    }
  }

  return hasValue ? total : null;
}

function resolveWeeklyColumnTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: MetricDefinition,
  periodId: PeriodId,
  asOf: Date,
): ResolvedTarget {
  const weekStart = periodId === "lastWeek" ? priorWeekMonday(asOf) : weekStartMonday(asOf);
  const monthAnchor = parseISO(`${monthStartForWeekMonday(weekStart)}T12:00:00`);
  const monthly = lookupTargetAtPeriod(
    index,
    scope,
    metric.targetMetric,
    "month",
    monthStartForWeekMonday(weekStart),
  );

  if (monthly != null) {
    const monthWeekFractionTotal = sumWeekFractionsInMonth(monthAnchor);
    const weekFraction = weekFractionInMonth(weekStart, monthAnchor);
    if (monthWeekFractionTotal > 0 && weekFraction > 0) {
      const target = monthly * (weekFraction / monthWeekFractionTotal);
      const pacingTarget = periodId === "wtd" ? getProratedWeeklyTarget(target, asOf) : target;

      return {
        target,
        pacingTarget,
        targetSource: "inferred",
      };
    }
  }

  const weekly = lookupWeeklyTarget(index, scope, metric.targetMetric, weekStart);

  if (!weekly) {
    return { target: null, pacingTarget: null, targetSource: null };
  }

  const pacingTarget =
    periodId === "wtd" ? getProratedWeeklyTarget(weekly.value, asOf) : weekly.value;

  return {
    target: weekly.value,
    pacingTarget,
    targetSource: weekly.source,
  };
}

function resolveMonthlyColumnTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: MetricDefinition,
  asOf: Date,
): ResolvedTarget {
  const value = lookupTargetAtPeriod(index, scope, metric.targetMetric, "month", monthStart(asOf));

  if (value == null) {
    return { target: null, pacingTarget: null, targetSource: null };
  }

  return {
    target: value,
    pacingTarget: getMtdPacingTarget(value, asOf),
    targetSource: "explicit",
  };
}

function resolveQuarterlyColumnTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: MetricDefinition,
  asOf: Date,
): ResolvedTarget {
  const value = lookupTargetAtPeriod(
    index,
    scope,
    metric.targetMetric,
    "quarter",
    quarterStart(asOf),
  );

  if (value == null) {
    return { target: null, pacingTarget: null, targetSource: null };
  }

  return {
    target: value,
    pacingTarget: getQtdPacingTarget(value, asOf),
    targetSource: "explicit",
  };
}

function resolveRollupTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: MetricDefinition,
  periodId: PeriodId,
  configByMetric: Map<string, TargetMetricConfig>,
  asOf: Date,
): ResolvedTarget {
  const rule = rollupRuleForColumn(metric, periodId, configByMetric);
  if (!rule) {
    return { target: null, pacingTarget: null, targetSource: null };
  }

  switch (rule) {
    case "sum-weeks-in-month": {
      const rolled = rollupWeeklyTargets(
        index,
        scope,
        metric.targetMetric,
        weekStartsOverlappingMonth(asOf),
        weekStartsOverlappingMonthThrough(asOf),
        asOf,
        "month",
      );
      if (!rolled) return { target: null, pacingTarget: null, targetSource: null };
      return {
        target: rolled.target,
        pacingTarget: rolled.pacingTarget,
        targetSource: rolled.source,
      };
    }
    case "sum-weeks-in-quarter": {
      const rolled = rollupWeeklyTargets(
        index,
        scope,
        metric.targetMetric,
        weekStartsOverlappingQuarter(asOf),
        weekStartsOverlappingQuarterThrough(asOf),
        asOf,
        "quarter",
      );
      if (!rolled) return { target: null, pacingTarget: null, targetSource: null };
      return {
        target: rolled.target,
        pacingTarget: rolled.pacingTarget,
        targetSource: rolled.source,
      };
    }
    case "sum-months-in-quarter": {
      const target = sumMonthlyTargetsForQuarter(index, scope, metric.targetMetric, asOf);
      if (target == null) return { target: null, pacingTarget: null, targetSource: null };
      const pacingTarget =
        sumMonthlyPacingTargetsForQuarter(index, scope, metric.targetMetric, asOf) ?? target;
      return {
        target,
        pacingTarget,
        targetSource: "inferred",
      };
    }
  }

  return { target: null, pacingTarget: null, targetSource: null };
}

export function resolveTargetForCell(
  metric: MetricDefinition,
  periodId: PeriodId,
  scope: TargetScope,
  targetIndex: Map<string, number>,
  configByMetric: Map<string, TargetMetricConfig>,
  asOf = nowToronto(),
): ResolvedTarget {
  if (periodId === "wtd" || periodId === "lastWeek") {
    return resolveWeeklyColumnTarget(targetIndex, scope, metric, periodId, asOf);
  }

  if (columnUsesMonthlyLookup(metric, periodId, configByMetric)) {
    return resolveMonthlyColumnTarget(targetIndex, scope, metric, asOf);
  }

  if (columnUsesQuarterlyLookup(metric, periodId, configByMetric)) {
    return resolveQuarterlyColumnTarget(targetIndex, scope, metric, asOf);
  }

  // Explicit quarter row wins over inferred sum of monthly targets.
  if (periodId === "qtd") {
    const explicitQuarter = resolveQuarterlyColumnTarget(targetIndex, scope, metric, asOf);
    if (explicitQuarter.target != null) return explicitQuarter;
  }

  const rollup = resolveRollupTarget(targetIndex, scope, metric, periodId, configByMetric, asOf);
  if (rollup.target != null) return rollup;

  return { target: null, pacingTarget: null, targetSource: null };
}

export function lookupEditorTarget(
  index: Map<string, number>,
  teamGroupKey: string,
  metric: string,
  periodType: TargetPeriodType,
  periodStart: string,
): number | null {
  return (
    index.get(targetKey("team_group", teamGroupKey, null, metric, periodType, periodStart)) ?? null
  );
}

export function departmentDefaultTarget(
  index: Map<string, number>,
  departmentId: string,
  metric: string,
  periodType: TargetPeriodType,
  periodStart: string,
): number | null {
  return (
    index.get(targetKey("department", departmentId, null, metric, periodType, periodStart)) ?? null
  );
}

/** Full-year target: sum explicit quarter rows, else sum explicit monthly rows. */
export function resolveAnnualTarget(
  index: Map<string, number>,
  scope: TargetScope,
  metric: MetricDefinition,
  asOf = nowToronto(),
): number | null {
  let total = 0;
  let hasQuarter = false;

  for (const quarterStartDate of quarterStartsInCalendarYear(asOf)) {
    const value = lookupTargetAtPeriod(
      index,
      scope,
      metric.targetMetric,
      "quarter",
      quarterStartDate,
    );
    if (value == null) continue;
    total += value;
    hasQuarter = true;
  }

  if (hasQuarter) return total;

  let hasMonth = false;
  for (const monthStartDate of allMonthStartsInCalendarYear(asOf)) {
    const value = lookupTargetAtPeriod(index, scope, metric.targetMetric, "month", monthStartDate);
    if (value == null) continue;
    total += value;
    hasMonth = true;
  }

  return hasMonth ? total : null;
}
